import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SettingsService } from 'src/app/services/settings.service';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { UserAppService } from 'src/app/services/user-app.service';
import { UserStateService } from 'src/app/services/user-state.service';

@Component({
  selector: 'app-sign-in',
  template: `
    <div class="signin-card">
      <h2 class="signin-title">Sign in</h2>
      <p class="signin-sub">Welcome back. Please enter your details.</p>

      <div *ngIf="ssoError" class="signin-alert">
        SSO login failed. Please try again or sign in with your credentials.
      </div>

      <form [formGroup]="credentials" (ngSubmit)="onSubmit()" class="signin-form">
        <div class="field">
          <label>Work email</label>
          <mat-form-field class="signin-field">
            <input
              matInput
              placeholder="you@company.com"
              formControlName="email"
              required
            />
            <mat-error
              *ngIf="credentials.get('email')?.invalid && credentials.get('email')?.touched"
            >
              Enter a valid email address
            </mat-error>
          </mat-form-field>
        </div>

        <div class="field">
          <label>Password</label>
          <mat-form-field class="signin-field">
            <input matInput formControlName="password" type="password" />
          </mat-form-field>
        </div>

        <button
          type="submit"
          class="btn-primary"
          [disabled]="!credentials.valid"
        >
          Log in
        </button>
      </form>

      <div *ngIf="SAML_CONFIGURATION_STATUS" class="signin-sso">
        <div class="signin-divider"><span>or</span></div>
        <button type="button" class="btn-ghost" (click)="ssoLogin()">
          Continue with SSO
        </button>
      </div>

      <div class="signin-signup" *ngIf="SIGN_UP_ALLOWED">
        <span>Don't have an account?</span>
        <a routerLink="./signup">Create account</a>
      </div>
    </div>
  `,
  styles: [`
    :host{ --brand:#E60000; --brand-h:#B80000; --ink:#1A1A1A; --muted:#6B7280; --line:#E5E7EB;
      font-family:'Inter',-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif}
    .signin-card{width:400px;max-width:100%;background:#fff;border:1px solid var(--line);
      border-radius:16px;padding:36px 34px;box-shadow:0 10px 30px rgba(0,0,0,.06);
      font-family:'Inter',-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif}
    .signin-title{font-size:24px;font-weight:700;color:var(--ink);margin:0}
    .signin-sub{color:var(--muted);font-size:14px;margin:6px 0 22px}
    .signin-alert{margin-bottom:16px;padding:11px 13px;border-radius:9px;
      border:1px solid #FBD5D5;background:#FFF5F5;color:#B42318;font-size:13px}
    .signin-form{display:flex;flex-direction:column}
    .field{margin-bottom:15px}
    .field label{display:block;font-size:13px;font-weight:600;color:var(--ink);margin-bottom:6px}
    .signin-field{width:100%}

    /* boxed filled mat-form-field -> clean D2C input */
    :host ::ng-deep .signin-field .mat-mdc-text-field-wrapper{
      background:#fff!important;border:1px solid var(--line);border-radius:9px;padding:0 12px}
    :host ::ng-deep .signin-field .mat-mdc-form-field-focus-overlay,
    :host ::ng-deep .signin-field .mat-mdc-form-field-subscript-wrapper{background:transparent}
    :host ::ng-deep .signin-field .mdc-line-ripple::before,
    :host ::ng-deep .signin-field .mdc-line-ripple::after{display:none}
    :host ::ng-deep .signin-field .mat-mdc-form-field-infix{padding:10px 0;min-height:unset}
    :host ::ng-deep .signin-field.mat-focused .mat-mdc-text-field-wrapper{
      border-color:var(--brand);box-shadow:0 0 0 3px rgba(230,0,0,.08)}

    .btn-primary{width:100%;height:46px;font-size:15px;font-weight:600;border-radius:9px;
      border:none;background:var(--brand);color:#fff;cursor:pointer;font-family:inherit;
      transition:background .15s;margin-top:6px}
    .btn-primary:hover{background:var(--brand-h)}
    .btn-primary:disabled{background:#F0A9A9;cursor:not-allowed}
    .btn-ghost{width:100%;height:46px;font-size:15px;font-weight:600;border-radius:9px;
      background:#fff;color:var(--brand);border:1px solid var(--brand);cursor:pointer;
      font-family:inherit;transition:background .15s}
    .btn-ghost:hover{background:#FFF5F5}

    .signin-divider{display:flex;align-items:center;text-align:center;color:var(--muted);
      font-size:12px;margin:18px 0}
    .signin-divider::before,.signin-divider::after{content:'';flex:1;height:1px;background:var(--line)}
    .signin-divider span{padding:0 12px}
    .signin-signup{margin-top:22px;text-align:center;font-size:14px;color:var(--muted)}
    .signin-signup a{color:var(--brand);font-weight:600;margin-left:6px;text-decoration:none}
  `],
})
export class SignInComponent implements OnInit {
  credentials = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', Validators.required),
  });

  SIGN_UP_ALLOWED: boolean = false;

  SAML_CONFIGURATION_STATUS: boolean = false;

  returnUrl = '/';

  ssoError = false;

  constructor(
    private userServices: UserAppService,
    private userStateService: UserStateService,
    private router: Router,
    private snackBar: SharedFeaturesService,
    private settingServices: SettingsService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.returnUrl = params['returnUrl'] || '/';
      this.returnUrl = this.returnUrl.replace(/\?next=%2F(?=solution_app)/, '');
      if (params['sso_error'] === '1' || params['sso_error'] === 'true') {
        this.ssoError = true;
        this.snackBar.openSnackBar('SSO login failed. Please try again or sign in with your credentials.', 'Close', 'error-snackbar');
        this.router.navigate([], { relativeTo: this.route, queryParams: { sso_error: null }, queryParamsHandling: 'merge', replaceUrl: true });
      }
    });
    this.settingServices.getSamlStatus().subscribe({
      next: (response: any) => {
        this.SAML_CONFIGURATION_STATUS = response?.saml;
      },
      error: (error: any) => {
        console.error('An error occurred', error);
      },
    });

    // Check if user is already logged in using cached state
    const userInfo = this.userStateService.getCurrentUserInfo();
    if (userInfo && 'username' in userInfo) {
      this.router.navigate(['/']);
    }

    this.userServices.getRegistrationAllowedStatus().subscribe({
      next: (response: any) => {
        this.SIGN_UP_ALLOWED = response.SIGN_UP_ALLOWED;
      },
      error: (error: any) => {
        console.debug(error);
      },
    });
  }

  async onSubmit() {
    if (this.credentials.valid) {
      let value = this.credentials.value;
      value.email = value?.email?.toLocaleLowerCase();
      this.userServices.login(value).subscribe({
        next: async (response: any) => {
          this.snackBar.openSnackBar(
            response['message'],
            'Close',
            'success-snackbar'
          );

          if (response?.redirect_to_admin) {
            window.location.href = '/admin/';
            return;
          }

          // Full page reload after login to get fresh CSRF token for the new session.
          // Without this, API calls would return 403 until manual refresh.
          if (/solution_app[^o]*o[^a]*authorize|download/.test(this.returnUrl)) {
            const protocol = this.getProtocolFromUrl(window.location.href);
            window.location.href =
              protocol +
              '://' +
              window.location.host +
              decodeURIComponent(this.returnUrl);
          } else {
            let targetUrl = this.returnUrl;
            if (this.returnUrl === '/') {
              await this.userStateService.loadUserInfo(true);
              targetUrl = this.userStateService.getDefaultRouteForUser();
            }
            window.location.href = targetUrl;
          }
        },
        error: (error) => {
          this.snackBar.openSnackBar(
            error.error.message || error.message,
            'Close',
            'error-snackbar'
          );
        },
      });
    }
  }

  ssoLogin() {
    const protocol = this.getProtocolFromUrl(window.location.href);
    window.location.href =
      protocol +
      '://' +
      window.location.host +
      '/saml2_auth/login' +
      '?next=' +
      this.returnUrl;
  }

  getProtocolFromUrl(url: string): string | null {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol.replace(':', '');
    } catch (error) {
      console.error('Error parsing URL:', error);
      return null;
    }
  }
}
